'use client';
import { useResponsive } from '@web-libs/core/responsiveness/use-responsive';
import { useAuth } from '@web-libs/features/auth/main/application/use-auth.concern';
import EmergencyIcon from '@web-libs/ui/svgs/icons/EmergencyIcon';
import { RoundButton } from '@web-libs/ui/ui-components/buttons/round-button';
import dynamic from 'next/dynamic';
import { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';
import { useOwnUser } from '../users/application/use-own-user.concern';

type EmergencyButtonProps = {
  size: number,
  svgSize: number,
  style?: CSSProperties
}
export const EmergencyButton = forwardRef<HTMLDivElement, EmergencyButtonProps>(
  (props, ref) => {
    const auth = useAuth();
    const { style, size, svgSize } = props;
    return (
      <div ref={ref} style={style} onClick={auth.emergencyLogout}>
        <RoundButton
          text="EXIT"
          color="#00AA68"
          size={size}
          svg={EmergencyIcon}
          svgSize={svgSize}
        />
      </div>
    );
  }
);
EmergencyButton.displayName = 'EmergencyButton';

/**
 *  FloatingEmergencyButton (Info)
 *  - You can use ".avoid-fab" class on other elements to avoid collisions with the floating emergency button.
 *  - You can use ".avoid-fab-vertical" class on other elements to avoid collisions with the floating emergency button vertically.
 */

type FloatingEmergencyButtonProps = {
  isLeft?: boolean,
  showAlways?: boolean,
  style?: CSSProperties
}
const _FloatingEmergencyButton = (props: FloatingEmergencyButtonProps) => {

  const { isTablet, isMobile } = useResponsive();

  const fabRef = useRef<HTMLDivElement>(null);
  const [isOffScreen, setIsOffScreen] = useState(false);
  const [verticalOffset, setVerticalOffset] = useState(0);
  const originalRect = useRef<DOMRect | null>(null);

  useEffect(() => {
    function isTooClose(rect1: DOMRect, rect2: DOMRect, minDist: number) {
      const bufferZone = {
        top: rect2.top - minDist,
        left: rect2.left - minDist,
        right: rect2.right + minDist,
        bottom: rect2.bottom + minDist
      };

      const isHorizontallyOverlapping = rect1.right >= bufferZone.left && rect1.left <= bufferZone.right;
      const isVerticallyOverlapping = rect1.bottom >= bufferZone.top && rect1.top <= bufferZone.bottom;

      return isHorizontallyOverlapping && isVerticallyOverlapping;
    }

    function runMainCheck(minDistance = 8) {
      if(!fabRef.current) return;

      setIsOffScreen(false);
      setVerticalOffset(0);

      const fab = fabRef.current;

      if (!originalRect.current) {
        originalRect.current = fab.getBoundingClientRect();
      }

      const fabRect = originalRect.current;

      // Check horizontal avoidance first
      const horizontalAvoidElements = Array.from(document.querySelectorAll('.avoid-fab:not(.avoid-fab-vertical)'));
      for (const el of horizontalAvoidElements) {
        const rect = el.getBoundingClientRect();
        if (isTooClose(fabRect, rect, minDistance)) {
          setIsOffScreen(true);
          break;
        }
      }

      // Check vertical avoidance
      const verticalAvoidElements = Array.from(document.querySelectorAll('.avoid-fab-vertical'));
      let maxVerticalOffset = 0;

      for (const el of verticalAvoidElements) {
        const rect = el.getBoundingClientRect();
        if (rect.right >= fabRect.left && rect.left <= fabRect.right) {
          // If vertically overlapping
          if (fabRect.bottom >= rect.top && fabRect.top <= rect.bottom) {
            // Calculate how much we need to move up
            const neededOffset = rect.top - fabRect.bottom - minDistance;
            maxVerticalOffset = Math.min(maxVerticalOffset, neededOffset);
          }
        }
      }

      if (maxVerticalOffset !== 0) {
        setVerticalOffset(maxVerticalOffset);
      }
    }

    let isChecking = false;

    function checkFabCollisions(minDistance = 8) {
      if (!fabRef.current || isChecking) return;
      isChecking = true;
      requestAnimationFrame(() => {
        runMainCheck(minDistance);
        isChecking = false;
      });
    }

    // Check collisions on mount and whenever the user resizes or scrolls
    const handleResizeOrScroll = () => checkFabCollisions();
    window.addEventListener('resize', handleResizeOrScroll);
    document.addEventListener('scroll', handleResizeOrScroll, { passive: true, capture: true });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
              if ( node instanceof HTMLElement && (node.matches('.avoid-fab') || node.querySelector('.avoid-fab') || node.matches('.avoid-fab-vertical') || node.querySelector('.avoid-fab-vertical'))) {
                checkFabCollisions();
                return;
              }
          });

          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement && (node.matches('.avoid-fab') || node.querySelector('.avoid-fab') || node.matches('.avoid-fab-vertical') || node.querySelector('.avoid-fab-vertical'))) {
              checkFabCollisions();
              return;
            }
          });
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Initial check
    setTimeout(() => runMainCheck(), 300);

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('resize', handleResizeOrScroll);
      document.removeEventListener('scroll', handleResizeOrScroll, { capture: true });
      observer.disconnect();
    };
  }, []);

  const style: CSSProperties = {
    position: 'fixed',
    bottom: 16,
    zIndex: 100,
    ...(props.isLeft ? { left: 16 } : { right: 16 }),
    transition: 'transform 0.5s ease-in-out',
    ...(isOffScreen ? { transform: 'translateX(90%)' } : {}),
    ...(verticalOffset !== 0 ? { transform: `translateY(${verticalOffset}px)` } : {})
  };

  if (isMobile) {
    return <EmergencyButton ref={fabRef} size={60} svgSize={32} style={style} />;
  }

  if (isTablet) {
    return <EmergencyButton ref={fabRef} size={64} svgSize={32} style={{ fontSize: 14, ...style }} />;
  }

  if (props.showAlways) {
    return <EmergencyButton ref={fabRef} size={100} svgSize={52} style={style} />;
  }

  return null;
};

export const FloatingEmergencyButton = dynamic(() => Promise.resolve(_FloatingEmergencyButton), {
  ssr: false
});

// Can only be used on dashboard, when own user context is available
export const FloatingEmergencyForClientsOnly = () => {
  const [showEmergencyButton, setShowEmergencyButton] = useState(false);
  const {isEmployee} = useOwnUser();

  useEffect(() => {
    isEmployee().then((isEmployee) => {
      if(!isEmployee) {
        setShowEmergencyButton(true);
      }
    });
  }, []);

  if(!showEmergencyButton) return null;
  return <FloatingEmergencyButton />;
}